import { ReactNode, useEffect, useState } from "react";
import Header from "./header";
import Sider from "antd/es/layout/Sider";
import { Breadcrumb, Layout, Menu, message } from "antd";
import {
  UnorderedListOutlined,
  VideoCameraOutlined,
  FlagOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  SettingOutlined,
  WechatWorkOutlined,
  TranslationOutlined,
  DollarOutlined,
  HistoryOutlined,
  FundViewOutlined,
  WechatOutlined,
  HomeOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  ApiOutlined,
  CarOutlined,
  TeamOutlined,
  UserOutlined,
  BellOutlined,
  RobotOutlined
} from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import { useNavigate } from "react-router-dom";
import { isNotNullUndefined, isNotNullUndefinedBlank } from "../helpers/common";
import { storage } from "../utils/storage";
import { useAuth } from "../context/AuthContext";
import { useFetchUser } from '../services/user.service';
import IResponse from "../types/response";
type MainLayoutProps = {
  children: ReactNode;
};


const MainLayout = ({ children }: MainLayoutProps) => {
  const navigate = useNavigate();
  const { hasPermission, permissionsLoaded, loadPermissions } = useAuth();
  const [userData, setUserData] = useState<any>();

  useEffect(() => {
    loadPermissions();
  }, [permissionsLoaded]);

  useEffect(() => {
    if (userData === null || userData === undefined || userData.length === 0) {
      fetchUserMutation.mutate({});
    }
  }, [])

  useEffect(() => {
    storage.setPreviousUrlPath(window.location.pathname);
  }, [window.location.pathname]);

  const fetchUserMutation = useFetchUser({
    onSuccess: (response: IResponse) => {
      if (response.statusCode === 200 && isNotNullUndefined(response.data)) {
        setUserData(response.data);
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    }
  });

  const menuConfig = [
    {
      icon: <DashboardOutlined />,
      label: "Dashboard",
      path: "/dashboard",
      permission: { module: "DASHBOARD", action: "VIEW" }
    },
    {
      icon: <RobotOutlined />,
      label: "AI Agent",
      path: "/ai-agent",
      permission: { module: "AI_AGENT", action: "VIEW" }
    },
    {
      icon: <CarOutlined />,
      label: "Trips",
      children: [
        {
          icon: <UnorderedListOutlined />,
          label: "Trip List",
          path: "/trips",
          permission: { module: "TRIPS", action: "VIEW" }
        },
        {
          icon: <VideoCameraOutlined />,
          label: "Content Submissions",
          path: "/tripmedia",
          permission: { module: "TRIP_MEDIA", action: "VIEW" }
        },
        {
          icon: <VideoCameraOutlined />,
          label: "Most Visited Places",
          path: "/mostvisitedplaces",
          permission: { module: "MOST_VISITED_PLACES", action: "VIEW" }
        },
        {
          icon: <HomeOutlined />,
          label: "Hotel & Airbnb",
          path: "/hotel&airbnb",
          permission: { module: "HOTEL_AIRBNB", action: "VIEW" }
        }
      ]
    },
    {
      icon: <TeamOutlined />,
      label: "Attendants",
      children: [
        {
          icon: <UnorderedListOutlined />,
          label: "Attendants",
          path: "/attendants",
          permission: { module: "ATTENDANTS", action: "VIEW" }
        },
        {
          icon: <DollarOutlined />,
          label: "Attendants Earning",
          path: "/attendants-earning-payment-history",
          permission: { module: "ATTENDANTS_EARNING", action: "VIEW" }
        }
      ]
    },
    {
      icon: <UserOutlined />,
      label: "Users",
      children: [
        {
          icon: <UnorderedListOutlined />,
          label: "Travellers",
          path: "/travellers",
          permission: { module: "TRAVELLERS", action: "VIEW" }
        },
        {
          icon: <UnorderedListOutlined />,
          label: "Admin Users",
          path: "/adminusers",
          permission: { module: "ADMIN_USERS", action: "VIEW" }
        }
      ]
    },
    {
      icon: <WechatOutlined />,
      label: "Communications",
      children: [
        {
          icon: <ApiOutlined />,
          label: "Marketing",
          path: "/marketing",
          permission: { module: "DASHBOARD", action: "VIEW" }
        },
        {
          icon: <BellOutlined />,
          label: "Push Notifications",
          path: "/push-notifications",
          permission: { module: "DASHBOARD", action: "VIEW" }
        },
        {
          icon: <WechatWorkOutlined />,
          label: "Chats",
          path: "/chats",
          permission: { module: "CHATS", action: "VIEW" }
        },
        {
          icon: <WechatOutlined />,
          label: "Admin Messages",
          path: "/adminmessages",
          permission: { module: "ADMIN_MESSAGES", action: "VIEW" }
        }
      ]
    },
    {
      icon: <DollarOutlined />,
      label: "Finance",
      children: [
        {
          icon: <FundViewOutlined />,
          label: "Finance",
          path: "/finance",
          permission: { module: "FINANCE", action: "VIEW" }
        },
        {
          icon: <DollarOutlined />,
          label: "Referral & Discount",
          path: "/refer-commission-and-discount-history",
          permission: { module: "REFERRAL_DISCOUNT", action: "VIEW" }
        },
        {
          icon: <UnorderedListOutlined />,
          label: "Commission",
          path: "/refer-commission-summary",
          permission: { module: "COMMISSION", action: "VIEW" }
        },
        {
          icon: <HistoryOutlined />,
          label: "Referral Payment",
          path: "/refer-payment-history",
          permission: { module: "REFERRAL_PAYMENT", action: "VIEW" }
        },
        {
          icon: <UnorderedListOutlined />,
          label: "ReferralCode Panel",
          path: "/refercode",
          permission: { module: "REFERRAL_CODE", action: "VIEW" }
        }
      ]
    },
    {
      icon: <EnvironmentOutlined />,
      label: "Locations",
      children: [
        {
          icon: <FlagOutlined />,
          label: "Countries",
          path: "/countries",
          permission: { module: "COUNTRIES", action: "VIEW" }
        },
        {
          icon: <EnvironmentOutlined />,
          label: "States / Province",
          path: "/states",
          permission: { module: "STATES", action: "VIEW" }
        },
        {
          icon: <ShopOutlined />,
          label: "Cities",
          path: "/cities",
          permission: { module: "CITIES", action: "VIEW" }
        }
      ]
    },
    {
      icon: <SettingOutlined />,
      label: "System",
      children: [
        {
          icon: <TranslationOutlined />,
          label: "Language Interface",
          path: "/languageinterface",
          permission: { module: "LANGUAGE_INTERFACE", action: "VIEW" }
        },
        {
          icon: <SettingOutlined />,
          label: "General Setting",
          path: "/settings",
          permission: { module: "SETTINGS", action: "VIEW" }
        },
        {
          icon: <UnorderedListOutlined />,
          label: "App Version",
          path: "/appversion",
          permission: { module: "APP_VERSION", action: "VIEW" }
        },
        {
          icon: <ClockCircleOutlined />,
          label: "Activity Logs",
          path: "/activitylogs",
          permission: { module: "ACTIVITY_LOGS", action: "VIEW" }
        }
      ]
    }
  ];

  const buildMenuItems = (items: typeof menuConfig): any[] =>
    items
      .map(item => {
        if ("children" in item && item.children) {
          const allowedChildren = buildMenuItems(item.children as typeof menuConfig);
          if (allowedChildren.length === 0) return null;
          return {
            key: item.label,
            icon: item.icon,
            label: item.label,
            children: allowedChildren
          };
        }
        const leaf = item as { path: string; permission: { module: string; action: string } };
        if (!hasPermission(leaf.permission.module, leaf.permission.action)) return null;
        return {
          key: leaf.path,
          icon: item.icon,
          label: item.label
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

  const allowedMenuItems = buildMenuItems(menuConfig);

  const defaultOpenKeys = menuConfig
    .filter(
      (item): item is typeof item & { children: { path: string }[] } =>
        "children" in item && Array.isArray(item.children)
    )
    .filter(item => item.children.some(child => child.path === window.location.pathname))
    .map(item => item.label);

  return (
    <Layout>
      <Header userData={userData} />
      <Layout>
        <Sider width={250} style={{ backgroundColor: '#1a1a2e' }}>
          <Menu
            className="h-[calc(100vh-75px)] min-h-full sidebar-menu-active text-base overflow-scroll fixed w-[250px] pb-16"
            rootClassName="mainLayoutMenu"
            theme="light"
            mode="inline"
            style={{ backgroundColor: '#1a1a2e' }}
            selectedKeys={[window.location.pathname]}
            defaultOpenKeys={defaultOpenKeys}
            onClick={(e) => navigate(e.key)}
            items={allowedMenuItems}
          />
        </Sider>

        <Layout className="p-6 min-h-[calc(100vh-75px)]">
          <Breadcrumb className="mx-0 text-base ">
            {/* <Breadcrumb.Item>Agent Listing</Breadcrumb.Item> */}
            {/* <Breadcrumb.Item>John Brown</Breadcrumb.Item> */}
          </Breadcrumb>
          <Content className="p-6 m-0" style={{ background: window.location.pathname === '/dashboard' ? '#f5f6fa' : '#ffffff' }}>
            {children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MainLayout;